import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import LiveEditing from './Components/LiveEditing';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import { ContactUsBlock } from './Components/Blocks/ContactUsBlock';
import { EducationBlock } from './Components/Blocks/EducationBlock';
import { Newsletter } from './Components/Blocks/Newsletter';
import { Registration } from './Components/Blocks/Registration';
import { CardCarouselBlock } from './Components/Blocks/CardCarouselBlock';
import LanguageSelector from './Components/LanguageSelector';
import MediaArchive from './Components/MediaArchive/MediaArchive';
import CaseBlock from './Components/Blocks/CaseBlock';
import ProductQuantityField from './Components/ProductQuantityField';
import DiscountLabel from './Components/DiscountLabel';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.querySelectorAll('.case-block-carousel').length > 0) {
        Array.from(document.querySelectorAll('.case-block-carousel')).forEach(
            (rootElement) => {
                const dataset = rootElement.dataset.cases;

                if (!dataset) {
                    return;
                }

                const cases = JSON.parse(dataset);

                Array.from(rootElement.querySelectorAll('template')).forEach(
                    (img, index) => {
                        if (cases[index]) {
                            cases[index].imageData = {
                                html: img.innerHTML,
                                src: img.dataset.src,
                            };
                        }
                    }
                );

                renderReact(
                    <Provider store={store}>
                        <CaseBlock cases={cases} />
                    </Provider>,
                    rootElement
                );
            }
        );
    }
    if (document.getElementById('contactusblock-form')) {
        let dataset = document.getElementById('contactusblock-form').dataset
            .form;
        let form;

        if (dataset) form = JSON.parse(dataset);

        renderReact(
            <Provider store={store}>
                <ContactUsBlock model={form} />
            </Provider>,
            document.getElementById('contactusblock-form')
        );
    }

    if (document.getElementById('educationblock-form')) {
        let dataset = document.getElementById('educationblock-form').dataset
            .form;
        let form;

        if (dataset) form = JSON.parse(dataset);

        renderReact(
            <Provider store={store}>
                <EducationBlock model={form} />
            </Provider>,
            document.getElementById('educationblock-form')
        );
    }
    if (document.getElementById('newsletter-form')) {
        let dataset = document.getElementById('newsletter-form').dataset.form;
        let form;

        if (dataset) form = JSON.parse(dataset);

        renderReact(
            <Provider store={store}>
                <Newsletter model={form} />
            </Provider>,
            document.getElementById('newsletter-form')
        );
    }
    if (document.getElementById('registration-form')) {
        let dataset = document.getElementById('registration-form').dataset.form;
        let form;

        if (dataset) form = JSON.parse(dataset);

        renderReact(
            <Provider store={store}>
                <Registration model={form} />
            </Provider>,
            document.getElementById('registration-form')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        let canEdit = document.getElementById('myPagePersons').dataset.canEdit;

        if (canEdit) {
            canEdit = JSON.parse(canEdit);
        }

        renderReact(
            <Provider store={store}>
                <PersonList canEdit={canEdit} />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        let canEdit = document.getElementById('myPageAddresses').dataset
            .canEdit;

        if (canEdit) {
            canEdit = JSON.parse(canEdit);
        }

        renderReact(
            <Provider store={store}>
                <AddressList canEdit={canEdit} />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('dinBoxCheckoutCart')) {
        const Cart = DynamicComponent({
            loader: () => import('./Components/Cart'),
        });
        renderReact(
            <Provider store={store}>
                <Cart
                    reloadPaymentOnChange={false}
                    reloadPageOnChange={true}
                    containerClassName="row checkout__container dinbox-checkout__cart-react"
                />
            </Provider>,
            document.getElementById('dinBoxCheckoutCart')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (
        document.getElementsByClassName('cardcarouselblock__carousel').length >
        0
    ) {
        Array.from(
            document.getElementsByClassName('cardcarouselblock__carousel')
        ).forEach((carousel) => {
            const cards = JSON.parse(carousel.dataset.cards);
            const articleTitleColor = carousel.dataset.articleTitleColor || '';
            renderReact(<CardCarouselBlock cards={cards} articleTitleColor={articleTitleColor} />, carousel);
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('.svg-icon').length > 0) {
        const SvgIcon = DynamicComponent({
            loader: () => import('./Components/SvgIcon'),
        });

        Array.from(document.querySelectorAll('.svg-icon')).forEach((item) => {
            const { name } = item.dataset;

            renderReact(
                <Provider store={store}>
                    <SvgIcon name={name}></SvgIcon>
                </Provider>,
                item
            );
        });
    }

    if (document.querySelectorAll('.discount-label').length > 0) {
        Array.from(document.querySelectorAll('.discount-label')).forEach(
            (item) => {
                const { text, additionalClass } = item.dataset;

                renderReact(
                    <Provider store={store}>
                        <DiscountLabel
                            text={text}
                            additionalClass={additionalClass}
                        ></DiscountLabel>
                    </Provider>,
                    item
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('langSelector')) {
        renderReact(
            <Provider store={store}>
                <LanguageSelector />
            </Provider>,
            document.getElementById('langSelector')
        );
    }

    if (document.getElementById('mediaArchive')) {
        const div = document.getElementById('mediaArchive');
        const model = JSON.parse(div.dataset.model);
        renderReact(
            <Provider store={store}>
                <MediaArchive model={model} />
            </Provider>,
            document.getElementById('mediaArchive')
        );
    }

    if (document.getElementById('BuyProductContainer')) {
        const element = document.getElementById('BuyProductContainer');
        renderReact(
            <Provider store={store}>
                <ProductQuantityField
                    minArticleQuantity={element.dataset.minArticleQuantity}
                />
            </Provider>,
            document.getElementById('BuyProductContainer')
        );
    }
    if (document.getElementById('GeneratedProductSheet')) {
        const PdfButton = DynamicComponent({
            loader: () => import('./Components/GeneratePdfButton'),
        });

        renderReact(
            <Provider store={store}>
                <PdfButton/>
            </Provider>,
            document.getElementById('GeneratedProductSheet')
        );
    }
};

bootstrapComponents();
